/**
 * Manages groups of checkboxes where only one can be selected at a time
 */
export class CheckboxGroupManager {
  private checkboxGroups: Map<string, HTMLInputElement[]> = new Map();
  private submitDelay: number;

  /**
   * Create a new CheckboxGroupManager
   * @param checkboxSelector CSS selector for checkboxes in groups
   * @param submitDelay Delay before auto-submitting form (ms)
   */
  constructor(
    private checkboxSelector: string = '[data-checkbox-group]',
    submitDelay: number = 100
  ) {
    this.submitDelay = Math.max(0, submitDelay); // Ensure non-negative
    this.initialize();
  }

  /**
   * Initialize the manager and set up event listeners
   */
  private initialize(): void {
    document.addEventListener('DOMContentLoaded', () => this.setupGroups());
  }

  /**
   * Find and organize checkboxes into their groups
   */
  private setupGroups(): void {
    const checkboxes = document.querySelectorAll<HTMLInputElement>(this.checkboxSelector);

    checkboxes.forEach(checkbox => {
      const groupName = checkbox.dataset.checkboxGroup?.toLowerCase();

      if (!groupName) {
        console.warn('Checkbox missing group name:', checkbox);
        return;
      }

      this.addToGroup(groupName, checkbox);
    });

    this.setupEventListeners();
  }

  /**
   * Add a checkbox to its corresponding group
   */
  private addToGroup(groupName: string, checkbox: HTMLInputElement): void {
    if (!this.checkboxGroups.has(groupName)) {
      this.checkboxGroups.set(groupName, []);
    }
    this.checkboxGroups.get(groupName)?.push(checkbox);
  }

  /**
   * Set up change event listeners for all checkboxes
   */
  private setupEventListeners(): void {
    this.checkboxGroups.forEach(group => {
      group.forEach(checkbox => {
        checkbox.addEventListener('change', () => this.handleCheckboxChange(checkbox, group));
      });
    });
  }

  /**
   * Handle checkbox change events
   */
  private handleCheckboxChange(activeCheckbox: HTMLInputElement, group: HTMLInputElement[]): void {
    if (!activeCheckbox.checked) return;

    // Uncheck other boxes in the group
    group.forEach(checkbox => {
      if (checkbox !== activeCheckbox) {
        checkbox.checked = false;
      }
    });

    // Submit the form if available
    this.maybeSubmitForm(activeCheckbox);
  }

  /**
   * Submit the containing form after a delay if it exists
   */
  private maybeSubmitForm(checkbox: HTMLInputElement): void {
    if (!checkbox.form) return;

    setTimeout(() => {
      checkbox.form?.requestSubmit();
    }, this.submitDelay);
  }

  /**
   * Manually initialize with custom selector and delay
   */
  public static init(
    selector: string = '[data-checkbox-group]',
    delay: number = 100
  ): CheckboxGroupManager {
    return new CheckboxGroupManager(selector, delay);
  }
}

// Initialize automatically when imported
CheckboxGroupManager.init();
